import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldAlert, Lock } from 'lucide-react';
import { isTauri } from '../utils/tauri';
import {
  useVaultStore,
  useVaultMasterStore,
  openVaultGate,
  refreshVaultMaster,
} from '../store/vaultStore';
import './Vault.css';

/**
 * Two one-line prompts above the item list.
 *
 * **The migration banner (§6.5)** — a vault that has never had a passphrase can
 * only be opened on this computer, so if the keychain is reset or the machine
 * is replaced there is no way back in. It reappears **once per app launch** and
 * has **no "don't show again"** (hazard H2): forcing it at launch invites a
 * throwaway passphrase, which is itself a data-loss generator, but hiding it
 * forever means the feature never lands on the vaults that need it most.
 *
 * **The locked line (§6.7)** — a protected vault that does not auto-unlock
 * fails *silently* everywhere else: passkeys stop working, autofill stops
 * working, saved SFTP connections stop appearing, and none of those surfaces
 * says why. This line is the only thing that connects the symptom to the cause,
 * and it is deliberately a status line rather than a launch modal.
 */

// Per launch, not persisted. A restart is a new chance to ask, which is the
// whole point of "deferrable, not permanently dismissible".
let migrationDismissedThisLaunch = false;

export default function VaultPassphraseBanner() {
  const status = useVaultStore((s) => s.status);
  const master = useVaultMasterStore((s) => s.master);
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(migrationDismissedThisLaunch);

  useEffect(() => {
    if (!isTauri()) return undefined;
    refreshVaultMaster();
    const onChanged = () => refreshVaultMaster();
    window.addEventListener('vault-master-changed', onChanged);
    return () => window.removeEventListener('vault-master-changed', onChanged);
  }, []);

  if (!isTauri()) return null;

  // `status.protected` fails CLOSED Rust-side: a vault whose kdf block is
  // present but unreadable reports protected, so an unreadable block can never
  // put "set a passphrase" in front of someone who already has one.
  const initialized = !!status?.initialized;
  const isProtected = !!status?.protected;
  const unlocked = !!status?.unlocked;
  const remembered = master ? !!master.remembered : !!status?.remembered;
  const keyMissing = !!master?.keyMissing;

  // A keyblock from a newer Apiboo (card 1.1): VaultUpdateRequiredBanner owns
  // that line. "Unlock" or "set a passphrase" here would be the wrong offer.
  if (master?.updateRequired) return null;

  // The file and this computer's key state disagree, so no secret opens this
  // vault. "Set a passphrase" is exactly the wrong offer — it points at
  // overwriting the file that needs restoring — and it is what the migration
  // condition below would otherwise render, since the mode reads keyringOnly.
  // This line is not dismissible: nothing in the vault works until it is dealt
  // with.
  if (keyMissing) {
    return (
      <div className="vault-pass-banner vault-pass-banner--warn">
        <ShieldAlert size={15} className="vault-pass-banner-icon" />
        <div className="vault-pass-banner-texts">
          <span className="vault-pass-banner-title">
            {t('vault.keyMissingBannerTitle', 'This vault does not match this computer')}
          </span>
          <span className="vault-pass-banner-text">
            {t(
              'vault.keyMissingBannerBody',
              'The vault file here is older than the key Apiboo has for it, so nothing can open it. Your passphrase is not the problem.'
            )}
          </span>
        </div>
        <div className="vault-pass-banner-actions">
          <button
            type="button"
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors modern-button text-sm"
            onClick={() => openVaultGate('vault')}
          >
            {t('vault.keyMissingBannerPrimary', 'What to do')}
          </button>
        </div>
      </div>
    );
  }

  if (initialized && !isProtected && !dismissed) {
    return (
      <div className="vault-pass-banner vault-pass-banner--warn">
        <ShieldAlert size={15} className="vault-pass-banner-icon" />
        <div className="vault-pass-banner-texts">
          <span className="vault-pass-banner-title">
            {t(
              'vault.migrateBannerTitle',
              'Set a passphrase to use this vault on your other devices'
            )}
          </span>
          <span className="vault-pass-banner-text">
            {t(
              'vault.migrateBannerBody',
              "Right now this vault can only be opened on this computer. If this computer's keychain is reset or the machine is replaced, there is no way to get back in."
            )}
          </span>
        </div>
        <div className="vault-pass-banner-actions">
          <button
            type="button"
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors modern-button text-sm"
            onClick={() => openVaultGate('migrate')}
          >
            {t('vault.migrateBannerPrimary', 'Set a passphrase')}
          </button>
          <button
            type="button"
            className="vault-pass-linkbtn"
            onClick={() => {
              migrationDismissedThisLaunch = true;
              setDismissed(true);
            }}
          >
            {t('vault.migrateBannerLater', 'Not now')}
          </button>
        </div>
      </div>
    );
  }

  if (isProtected && !remembered && !unlocked) {
    return (
      <div className="vault-pass-banner">
        <Lock size={15} className="vault-pass-banner-icon" />
        <div className="vault-pass-banner-texts">
          <span className="vault-pass-banner-text">
            {t(
              'vault.lockedStartupLine',
              'Unlock your vault to use passkeys, autofill and saved SFTP connections'
            )}
          </span>
        </div>
        <div className="vault-pass-banner-actions">
          <button
            type="button"
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors modern-button text-sm"
            onClick={() => openVaultGate('vault')}
          >
            {t('vault.unlock', 'Unlock')}
          </button>
        </div>
      </div>
    );
  }

  return null;
}

/** Test seam: the per-launch dismissal is module state, not component state. */
export function __resetMigrationDismissal() {
  migrationDismissedThisLaunch = false;
}
